import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signIn, signUp, confirmSignUp, signOut, fetchAuthSession, fetchUserAttributes, resetPassword, confirmResetPassword, updatePassword } from 'aws-amplify/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAuthState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      await checkUserRole(currentUser);
    } catch (error) {
      setUser(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const checkUserRole = async (currentUser) => {
    try {
      const userAttributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const groups = session?.tokens?.idToken?.payload['cognito:groups'] || [];
      const userRole = userAttributes?.['custom:user_role'] || 'user';
      
      // Store user groups for more granular access control
      const isAdminUser = groups.includes('Admin') || groups.includes('Admins') || userRole === 'admin';
      setIsAdmin(isAdminUser);
      
      // Store additional role information in user object
      if (currentUser) {
        currentUser.groups = groups;
        currentUser.attributes = userAttributes;
        currentUser.roles = {
          isAdmin: isAdminUser,
          isModerator: groups.includes('Moderator') || groups.includes('Moderators'),
          isUser: true
        };
      }
      
      console.log('User roles:', { groups, isAdmin: isAdminUser });
    } catch (error) {
      console.error('Error checking user role:', error);
      setIsAdmin(false);
    }
  };

  const signInUser = async (username, password) => {
    try {
      const result = await signIn({ username, password });
      const user = await getCurrentUser();
      setUser(user);
      await checkUserRole(user);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const signUpUser = async (username, password, attributes = {}) => {
    try {
      const result = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email: username,
            'custom:user_role': 'user',
            'custom:created_date': new Date().toISOString(),
            ...attributes
          }
        }
      });
      return result;
    } catch (error) {
      throw error;
    }
  };

  const confirmSignUpUser = async (username, code) => {
    try {
      return await confirmSignUp({ username, confirmationCode: code });
    } catch (error) {
      throw error;
    }
  };

  const signOutUser = async () => {
    try {
      await signOut();
      setUser(null);
      setIsAdmin(false);
    } catch (error) {
      throw error;
    }
  };

  const forgotPassword = async (username) => {
    try {
      return await resetPassword({ username });
    } catch (error) {
      throw error;
    }
  };

  const forgotPasswordSubmit = async (username, code, newPassword) => {
    try {
      return await confirmResetPassword({ username, confirmationCode: code, newPassword });
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      return await updatePassword({ oldPassword, newPassword });
    } catch (error) {
      throw error;
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  };

  // Helper functions for role checking
  const hasRole = (role) => {
    return user?.groups?.includes(role) || false;
  };

  const hasAnyRole = (roles) => {
    return roles.some(role => hasRole(role));
  };

  const value = {
    user,
    loading,
    isAdmin,
    signIn: signInUser,
    signUp: signUpUser,
    confirmSignUp: confirmSignUpUser,
    signOut: signOutUser,
    forgotPassword,
    forgotPasswordSubmit,
    changePassword,
    checkAuthState,
    getAuthToken,
    hasRole,
    hasAnyRole,
    userGroups: user?.groups || [],
    userRoles: user?.roles || { isAdmin: false, isModerator: false, isUser: true }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};